import React from 'react';
import { supabase } from './lib/supabase';
import { useEffect, useState } from 'react';
import { Bot, X } from 'lucide-react';
import type { Route } from './components/Sidebar';
import { cn } from '@/lib/utils';

// Components
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import WorkOrders from './pages/WorkOrders';
import Customers from './pages/Customers';
import Team from './pages/Team';
import SiteMaker from './pages/site-maker';
import Invoices from './pages/invoices';
import CreateInvoice from './pages/invoices/CreateInvoice';
import RecurringOrders from './pages/recurring-orders';
import Expenses from './pages/expenses';
import Payments from './pages/payments';
import Settings from './pages/Settings';
import WorkMagic from './pages/work-magic/index';
import Login from './pages/Login';
import Register from './pages/Register';
import BusinessProfile from './components/BusinessProfile';
import AIHelper from './components/AIHelper';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentRoute, setCurrentRoute] = useState<Route>(
    (window.location.pathname.substring(1) || 'dashboard') as Route
  );
  const [userRole, setUserRole] = useState<'owner' | 'admin' | 'manager' | 'dispatcher' | 'technician' | 'office' | null>(null);
  const [showAIHelper, setShowAIHelper] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Add this flag to control AI Helper visibility
  const [aiHelperEnabled, setAIHelperEnabled] = useState(false); // Set to false to hide it

  useEffect(() => {
    // Check initial auth state
    supabase.auth.getSession().then(({ data: { session } }) => {
      const isAuthed = !!session;
      setIsAuthenticated(isAuthed);
      if (session?.user) {
        // Get user role from company_users table
        supabase
          .from('company_users')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('status', 'active')
          .maybeSingle()
          .then(({ data, error }) => {
            if (data && !error) {
              setUserRole(data.role as typeof userRole);
            } else {
              // If no role found, check if user is company owner
              supabase
                .from('companies')
                .select('id')
                .eq('id', session.user.id)
                .single()
                .then(async ({ data: companyData, error: companyError }) => {
                  if (companyData && !companyError) {
                    // If company exists but no role, they are the owner
                    setUserRole('owner');
                  } else {
                    console.error('Error checking company:', companyError);
                    setUserRole(null);
                  }
                });
            }
          });
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const isAuthed = !!session;
      setIsAuthenticated(isAuthed);
      if (session?.user) {
        supabase
          .from('company_users')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('status', 'active')
          .maybeSingle()
          .then(({ data, error }) => {
            if (data && !error) {
              setUserRole(data.role as typeof userRole);
            } else {
              // If no role found, check if user is company owner
              supabase
                .from('companies')
                .select('id')
                .eq('id', session.user.id)
                .single()
                .then(async ({ data: companyData, error: companyError }) => {
                  if (companyData && !companyError) {
                    // If company exists but no role, they are the owner
                    setUserRole('owner');
                  } else {
                    console.error('Error checking company:', companyError);
                    setUserRole(null);
                  }
                });
            }
          });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Handle navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.substring(1) || 'dashboard';
      if (!path.startsWith('site/') && isValidRoute(path)) {
        setCurrentRoute(path as Route);
      }
    };

    const handleRouteChange = (event: CustomEvent<Route>) => {
      setCurrentRoute(event.detail);
      const path = event.detail === 'dashboard' ? '' : event.detail;
      window.history.pushState({}, '', `/${path}`);
    };

    window.addEventListener('routeChange', handleRouteChange as EventListener);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('routeChange', handleRouteChange as EventListener);
    };
  }, []);

  // Helper function to validate routes
  const isValidRoute = (path: string): boolean => {
    const validRoutes: Route[] = [
      'dashboard', 'customers', 'work-orders', 'schedule', 
      'settings', 'work-magic', 'site-maker', 'team', 'invoices',
      'recurring-orders', 'expenses', 'payments'
    ];
    return validRoutes.includes(path as Route);
  };

  if (!isAuthenticated) {
    return window.location.pathname === '/register' ? (
      <Register />
    ) : (
      <Login />
    );
  }

  // Handle site preview and business profile routes
  if (window.location.pathname.startsWith('/site/preview/')) {
    const encodedData = window.location.pathname.split('/site/preview/')[1];
    try {
      // Convert URL-safe base64 back to regular base64
      const base64 = encodedData
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      // Add back padding if needed
      const padding = base64.length % 4;
      const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64;
      const config = JSON.parse(atob(paddedBase64));
      return <BusinessProfile config={config} />;
    } catch (err) {
      return <div>Invalid profile data</div>;
    }
  } else if (window.location.pathname.startsWith('/site/')) {
    const encodedData = window.location.pathname.split('/site/')[1];
    if (encodedData) {
      try {
        // Convert URL-safe base64 back to regular base64
        const base64 = encodedData
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        // Add back padding if needed
        const padding = base64.length % 4;
        const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64;
        const config = JSON.parse(atob(paddedBase64));
        return <BusinessProfile config={config} />;
      } catch (err) {
        return <div>Invalid profile data</div>;
      }
    }
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <Sidebar collapsed={sidebarCollapsed} onCollapse={setSidebarCollapsed} />

      {/* Main Content */}
      <div className={cn(
        "flex-1 flex flex-col min-w-0 transition-all duration-300",
        sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
      )}>
        {/* Sticky Header */}
        <Header collapsed={sidebarCollapsed} />

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto relative px-6 py-8">
          {currentRoute === 'dashboard' && <Dashboard />}
          {currentRoute === 'customers' && <Customers />}
          {currentRoute === 'team' && <Team />}
          {currentRoute === 'work-orders' && <WorkOrders />}
          {currentRoute === 'settings' && <Settings />}
          {currentRoute === 'work-magic' && <WorkMagic />}
          {currentRoute === 'site-maker' && <SiteMaker />}
          {currentRoute === 'recurring-orders' && <RecurringOrders />}
          {currentRoute === 'expenses' && <Expenses />}
          {currentRoute === 'payments' && <Payments />}
          {(currentRoute === 'invoices' || currentRoute === 'invoices/create') && (
            userRole && ['owner', 'admin', 'manager'].includes(userRole) ? (
              currentRoute === 'invoices/create' ? (
                <CreateInvoice
                  onClose={() => window.dispatchEvent(new CustomEvent('routeChange', { detail: 'invoices' }))}
                  onSubmit={async (invoice, items) => {
                    try {
                      // Add invoice logic here
                      window.dispatchEvent(new CustomEvent('routeChange', { detail: 'invoices' }));
                    } catch (err) {
                      console.error('Failed to create invoice:', err);
                      throw err;
                    }
                  }}
                />
              ) : (
                <Invoices />
              )
            ) : (
              <div className="p-6 max-w-lg mx-auto">
                <div className="bg-destructive/10 text-destructive p-6 rounded-xl shadow-sm">
                  <h3 className="text-xl font-semibold mb-2">Access Denied</h3>
                  <p>
                    You don't have permission to access the invoices section. This feature is only available to owners, administrators, and managers.
                  </p>
                  <p className="mt-4 text-sm">
                    Current role: {userRole || 'Unknown'}
                  </p>
                  <p className="mt-2 text-sm">
                    If you believe this is a mistake, please contact your administrator to update your role permissions.
                  </p>
                </div>
              </div>
            )
          )}
        </main>
      </div>

      {/* AI Helper - Only show if enabled */}
      {aiHelperEnabled && showAIHelper ? (
        <AIHelper onClose={() => setShowAIHelper(false)} />
      ) : aiHelperEnabled && !window.location.pathname.startsWith('/site/') && (
        <div className="fixed bottom-4 right-4 z-50">
          <button
            onClick={() => setShowAIHelper(true)}
            className="p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200"
            title="Show AI Helper"
          >
            <Bot className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
};

export default App;